/**
 * Error Handler Utilities
 * Provides centralized error handling and user-friendly error messages
 */

import { AxiosError } from 'axios';
import { showError, showWarning } from './notificationHelpers';
import { mapAppErrorMessage } from './errorMessages';
import type { AppError } from '../../types/errors';

/**
 * Map of error codes to user-friendly messages
 */
const ERROR_MESSAGES: Record<string, string> = {
  // Network errors
  'ERR_NETWORK': 'Network connection failed, please check your network settings',
  'ECONNABORTED': 'Request timeout, please try again later',
  
  // HTTP status codes
  '400': 'Invalid request parameters',
  '401': 'Authentication failed, please login again',
  '403': 'You do not have permission to access this resource',
  '404': 'Requested resource not found',
  '429': 'Too many requests, please try again later',
  '500': 'Internal server error',
  '502': 'Service temporarily unavailable, please try again later',
  '503': 'Service under maintenance, please try again later',
  
  // Business error codes
  'USER_NOT_FOUND': 'Incorrect email',
  'INVALID_CREDENTIALS': 'Invalid username or password',
  'EMAIL_ALREADY_EXISTS': 'This email is already registered',
  'INVALID_VERIFICATION_CODE': 'Verification code is invalid or expired',
  'SUBSCRIPTION_EXPIRED': 'Subscription has expired, please renew',
};

/**
 * Extract error message from various error response formats
 */
export function extractErrorMessage(error: unknown): string {
  if (error instanceof AxiosError) {
    const responseData = error.response?.data;
    
    // Check for custom error message in response
    if (responseData) {
      if (typeof responseData === 'string') return responseData;
      if (responseData.errorMessage) return responseData.errorMessage;
      if (responseData.message) return responseData.message;
      if (responseData.error) return responseData.error;
    }
    
    // Check for status code based message
    if (error.response?.status) {
      const statusMessage = ERROR_MESSAGES[error.response.status.toString()];
      if (statusMessage) return statusMessage;
    }
    
    // Check for network error
    if (error.code && ERROR_MESSAGES[error.code]) {
      return ERROR_MESSAGES[error.code];
    }
    
    // Fallback to error message
    if (error.message) return error.message;
  }
  
  // Handle regular errors
  if (error instanceof Error) {
    return error.message;
  }
  
  // Fallback
  return 'An unknown error occurred, please try again later';
}

/**
 * Handle API errors with appropriate notifications
 */
export function handleApiError(error: unknown, appError?: AppError, context?: string): void {
  let message: string;
  
  // Use AppError message if available
  if (appError) {
    message = mapAppErrorMessage(appError);
  } else {
    message = extractErrorMessage(error);
  }
  
  // Don't show notification for auth errors (they should be handled inline)
  if (error instanceof AxiosError) {
    const isAuthError = error.config?.url?.includes('/auth/');
    if (isAuthError) return;
    
    // Show warning for rate limiting
    if (error.response?.status === 429) {
      showWarning(message);
      return;
    }
  }
  
  // Show error notification
  showError(context ? `${context}: ${message}` : message);
}